import produce from 'immer';
import { cloneDeep } from 'lodash';
import { isEqual } from 'lodash/fp';

import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { VerificationCheckConfiguration } from 'dashboard/forms/FormVerificationTemplateChecks/FormVerificationTemplateChecks';
import { CheckStatus, RequiredProperty } from 'dashboard/models/check';
import { ExtractionCheckConfig } from 'dashboard/models/verification-check-config/id-extracted-properties-detection';
import { indexForOverride } from 'dashboard/models/verification-check-config/utils';
import { VerificationCheckConfig } from 'dashboard/models/verification-check-config/verification-check-config';
import { HStack } from 'design-system/components/Stack';
import { Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import { DEFAULT_CHECK_REASON_CONFIGS } from 'lib/constants/check';
import { CheckName } from 'lib/verificationCheck';

const EXTRACTED_PROPERTIES_REQUIRED: Record<keyof typeof RequiredProperty, string> = {
  Birthdate: 'Birthdate',
  IdentificationNumber: 'Identification Number',
  NameFirst: 'First Name',
  NameLast: 'Last Name',
  AddressStreet1: 'Street 1',
  AddressStreet2: 'Street 2',
  AddressCity: 'City',
  AddressSubdivision: 'Subdivision',
  AddressPostalCode: 'Postal Code',
  IssueDate: 'Issue Date',
  ExpirationDate: 'Expiration Date',
  DocumentNumber: 'Document Number',
};

export const REQUIRED_PROPERTIES_OPTIONS: OptionType<RequiredProperty>[] = Object.keys(
  RequiredProperty,
).map(
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  (property: keyof typeof RequiredProperty) => ({
    value: RequiredProperty[property],
    label: EXTRACTED_PROPERTIES_REQUIRED[property],
  }),
);

interface ExtractedPropertiesConfigurationElementProps {
  checkConfig: ExtractionCheckConfig;
  editable: boolean;
  handleUpdateCheck: (check: VerificationCheckConfig) => void;
}

const ExtractedPropertiesConfigurationElement = ({
  checkConfig,
  editable,
  handleUpdateCheck,
}: ExtractedPropertiesConfigurationElementProps) => {
  const overrideIndex = indexForOverride({
    attributesField: checkConfig['required-properties'],
    countryCode: 'default',
    idClass: 'default',
  });

  const updateRequiredProperties = (properties: RequiredProperty[]) => {
    const check = produce(checkConfig, (draft) => {
      if (
        overrideIndex in draft['required-properties'] &&
        draft['required-properties'][overrideIndex]
      ) {
        draft['required-properties'][overrideIndex].properties = properties;
      } else {
        draft['required-properties'].push({
          'id-class': 'default',
          'country-code': 'default',
          properties: properties,
        });
      }
    });
    handleUpdateCheck(check);
  };

  const toggleRequiredPropertiesAbsentOnId = (value: boolean) => {
    const reasonConfigs =
      cloneDeep(checkConfig['reason-configs']) ||
      cloneDeep(DEFAULT_CHECK_REASON_CONFIGS[CheckName.IdExtractedPropertiesDetection]);
    const reasonToUpdate = reasonConfigs.find(
      (r) => r.reason === 'required_properties_absent_on_id',
    );
    if (reasonToUpdate) {
      reasonToUpdate.status = value ? CheckStatus.Passed : CheckStatus.Failed;
    }

    const check = produce(checkConfig, (draft) => {
      // If we do not set reason configs back to undefined it will show up as a change in the differ
      if (
        isEqual(
          reasonConfigs,
          DEFAULT_CHECK_REASON_CONFIGS[CheckName.IdExtractedPropertiesDetection],
        )
      ) {
        draft['reason-configs'] = undefined;
      } else {
        draft['reason-configs'] = reasonConfigs;
      }
    });
    handleUpdateCheck(check);
  };

  const passVerificationIfFieldAbsent = checkConfig['reason-configs']
    ? checkConfig['reason-configs'].find((r) => r.reason === 'required_properties_absent_on_id')
        ?.status === CheckStatus.Passed
    : true;

  return (
    <VerificationCheckConfiguration
      title='Default Required Properties'
      disabled={!editable}
      helpText='Any countries that do not have custom required properties set in the enabled countries and ID types table will use these default required properties'
    >
      <ReactSelect
        options={REQUIRED_PROPERTIES_OPTIONS}
        disabled={!editable}
        label={
          <Paragraph>
            The default required properties for this check unless per-country/id overrides are
            specified
          </Paragraph>
        }
        isMulti
        value={checkConfig['required-properties'][overrideIndex]?.properties}
        onChange={updateRequiredProperties}
      />
      <HStack>
        <Paragraph>Pass when required property does not appear on the ID</Paragraph>
        <ToggleSlider
          onChange={toggleRequiredPropertiesAbsentOnId}
          disabled={false}
          value={passVerificationIfFieldAbsent}
        />
        <HelpIcon>
          Currently, the properties we support for this option are birthdate, issue date, and
          expiration date
        </HelpIcon>
      </HStack>
    </VerificationCheckConfiguration>
  );
};

export default ExtractedPropertiesConfigurationElement;
